import Navbar from "@/components/Layout/Navbar";
import Footer from "@/components/Layout/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import { Link } from "@/lib/router-compat";
import SEOHead from "@/components/SEOHead";
import { PAGE_SEO_CONFIG } from "@/lib/seo";

import nearshorGuideImage from "@/assets/blog/nearshore-guide-hero.jpg";
import egyptOutsourcingImage from "@/assets/blog/egypt-outsourcing-hero.jpg";
import outsourcingModelsImage from "@/assets/blog/outsourcing-models-hero.jpg";

const blogPosts = [
  {
    slug: "nearshore-outsourcing-guide",
    title: "The Definitive Guide for Nearshore Outsourcing Software Services in Africa",
    excerpt: "Africa is rapidly emerging as an attractive destination for nearshore software development outsourcing. Discover the benefits, challenges, and top destinations.",
    date: "2026-01-10",
    readTime: "12 min read",
    category: "Guides",
    image: nearshorGuideImage,
  },
  {
    slug: "egypt-outsourcing-costs",
    title: "Egypt Software Outsourcing Cuts Tech Costs by 70%, Study Shows",
    excerpt: "Discover how companies achieve remarkable cost savings by outsourcing software development to Egypt. Learn about pricing comparisons, success stories, and implementation strategies.",
    date: "2026-02-05",
    readTime: "10 min read",
    category: "Cost Analysis",
    image: egyptOutsourcingImage,
  },
  {
    slug: "outsourcing-models-comparison",
    title: "Nearshoring vs. Offshoring vs. Onshoring vs. Outsourcing: What's the Difference?",
    excerpt: "Understanding the differences between nearshoring, offshoring, onshoring, and outsourcing. Learn which model is right for your business with expert insights and real-world examples.",
    date: "2026-02-14",
    readTime: "15 min read",
    category: "Comparison",
    image: outsourcingModelsImage,
  },
];

const Blog = () => {
  const seoConfig = PAGE_SEO_CONFIG.blog;

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        titleEn={seoConfig.titleEn}
        titleDe={seoConfig.titleDe}
        descriptionEn={seoConfig.descriptionEn}
        descriptionDe={seoConfig.descriptionDe}
        keywords={seoConfig.keywords}
        path={seoConfig.path}
        breadcrumbs={[
          { name: 'Home', nameDE: 'Startseite', url: '/' },
          { name: 'Blog', nameDE: 'Blog', url: '/blog' }
        ]}
      />

      <Navbar />

      <main className="pt-24">
        {/* Hero */}
        <section className="py-32">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto text-center">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">Insights</div>
              <h1 className="text-5xl md:text-6xl font-semibold tracking-[-0.035em] text-balance mb-6">
                Nearshore.Africa Blog
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Expert insights, guides, and trends in nearshore software development outsourcing
              </p>
            </div>
          </div>
        </section>

        {/* Posts */}
        <section className="pb-32">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-7xl mx-auto">
              {blogPosts.map((post) => (
                <Card key={post.slug} className="group overflow-hidden">
                  <CardContent className="p-0">
                    <div className="relative overflow-hidden">
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="w-full h-52 object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 rounded-full border border-border/60 bg-background/80 backdrop-blur-md text-xs uppercase tracking-[0.15em] text-foreground/80">
                          {post.category}
                        </span>
                      </div>
                    </div>
                    
                    <div className="p-7">
                      <div className="flex items-center gap-4 text-xs uppercase tracking-[0.12em] text-muted-foreground mb-4">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5" />
                          <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5" />
                          <span>{post.readTime}</span>
                        </div>
                      </div>
                      
                      <h2 className="text-xl font-semibold tracking-[-0.02em] mb-3 leading-snug">
                        {post.title}
                      </h2>
                      
                      <p className="text-sm text-muted-foreground mb-6 line-clamp-3 leading-relaxed">
                        {post.excerpt}
                      </p>
                      
                      <Button 
                        variant="link"
                        className="group/btn p-0 h-auto font-medium text-foreground"
                        asChild
                      >
                        <Link to={`/blog/${post.slug}`} aria-label={`Read full article: ${post.title}`}>
                          Read article
                          <ArrowRight className="ml-1 w-3.5 h-3.5 transition-transform group-hover/btn:translate-x-1" aria-hidden="true" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Subscribe */}
            <div className="mt-24 max-w-3xl mx-auto">
              <Card>
                <CardContent className="p-10 md:p-14 text-center">
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-5">Newsletter</div>
                  <h3 className="text-3xl md:text-4xl font-semibold tracking-[-0.028em] mb-4">Stay Updated</h3>
                  <p className="text-muted-foreground mb-8 max-w-xl mx-auto leading-relaxed">
                    Get the latest insights on nearshore software development outsourcing delivered to your inbox.
                  </p>
                  <Button size="lg" asChild>
                    <a href="https://www.evolvice.de/en" target="_blank" rel="noopener noreferrer" aria-label="Subscribe to Nearshore.Africa updates via Evolvice (opens in new tab)">
                      Subscribe Now
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default Blog;
